import { useEffect, useRef } from 'react';
import './ZapCannonAnimation.css';

export default function ZapCannonAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for both Pokemon
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Calculate direction for cannon projectile
    const dx = defenderCenterX - attackerCenterX;
    const dy = defenderCenterY - attackerCenterY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];
    
    // PHASE 1: ELECTRIC CHARGE BUILDUP (0-600ms)
    // Initial attacker animation - massive electric charge
    attackerEl.style.transition = 'transform 0.6s ease-in-out, filter 0.6s ease-in-out';
    attackerEl.style.transform = 'scale(1.2)';
    attackerEl.style.filter = 'brightness(1.5) drop-shadow(0 0 25px #FFD700)';
    
    // Create massive electric aura around attacker
    const electricAura = document.createElement('div');
    electricAura.className = 'zap-cannon-electric-aura';
    electricAura.style.left = `${attackerCenterX}px`;
    electricAura.style.top = `${attackerCenterY}px`;
    container.appendChild(electricAura);
    elements.push(electricAura);
    
    // Create electric charge sparks around attacker
    for (let i = 0; i < 24; i++) {
      const sparkTimer = setTimeout(() => {
        const spark = document.createElement('div');
        spark.className = 'zap-cannon-charge-spark';
        
        const sparkAngle = (i / 24) * Math.PI * 2;
        const sparkDistance = 50 + Math.random() * 30;
        spark.style.left = `${attackerCenterX + Math.cos(sparkAngle) * sparkDistance}px`;
        spark.style.top = `${attackerCenterY + Math.sin(sparkAngle) * sparkDistance}px`;
        spark.style.setProperty('--spark-delay', `${i * 0.02}s`);
        spark.style.setProperty('--spark-duration', `${0.4 + Math.random() * 0.3}s`);
        
        container.appendChild(spark);
        elements.push(spark);
        
        // Auto-remove spark
        const sparkRemoveTimer = setTimeout(() => {
          if (spark.isConnected) {
            spark.remove();
          }
        }, 800);
        timers.push(sparkRemoveTimer);
      }, i * 25);
      timers.push(sparkTimer);
    }

    // Create electric field distortion
    const electricField = document.createElement('div');
    electricField.className = 'zap-cannon-electric-field';
    electricField.style.left = `${attackerCenterX}px`;
    electricField.style.top = `${attackerCenterY}px`;
    container.appendChild(electricField);
    elements.push(electricField);

    // PHASE 2: CANNON BALL FORMATION (600-900ms)
    const cannonBallTimer = setTimeout(() => {
      // Create the electric cannon ball at attacker position
      const cannonBall = document.createElement('div');
      cannonBall.className = 'zap-cannon-ball';
      cannonBall.style.left = `${attackerCenterX}px`;
      cannonBall.style.top = `${attackerCenterY}px`;
      cannonBall.style.setProperty('--start-x', `${attackerCenterX}px`);
      cannonBall.style.setProperty('--start-y', `${attackerCenterY}px`);
      cannonBall.style.setProperty('--target-x', `${defenderCenterX}px`);
      cannonBall.style.setProperty('--target-y', `${defenderCenterY}px`);
      cannonBall.style.setProperty('--dx', `${dx}px`);
      cannonBall.style.setProperty('--dy', `${dy}px`);
      
      container.appendChild(cannonBall);
      elements.push(cannonBall);
      
      // Create electric trail particles following the cannon ball
      for (let i = 0; i < 15; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'zap-cannon-trail-spark';
          
          // Calculate position along the trajectory
          const progress = (i / 15) * 0.8; // Trail follows behind
          const trailX = attackerCenterX + (dx * progress);
          const trailY = attackerCenterY + (dy * progress);
          
          trail.style.left = `${trailX}px`;
          trail.style.top = `${trailY}px`;
          trail.style.setProperty('--trail-delay', `${i * 0.03}s`);
          
          container.appendChild(trail);
          elements.push(trail);
          
          const trailRemoveTimer = setTimeout(() => {
            if (trail.parentNode) {
              trail.remove();
            }
          }, 1000);
          timers.push(trailRemoveTimer);
        }, i * 40);
        timers.push(trailTimer);
      }
      
    }, 600);
    timers.push(cannonBallTimer);
        
    // PHASE 3: MASSIVE ELECTRIC EXPLOSION (1200-1800ms)
    const explosionTimer = setTimeout(() => {
      // Make defender react with electric paralysis animation
      defenderEl.classList.add('zap-cannon-paralysis');
      defenderEl.style.filter = 'brightness(2) hue-rotate(60deg) saturate(2)';
      defenderEl.style.transform = 'scale(0.95)';
      
      // Create massive electric explosion at opponent
      const explosion = document.createElement('div');
      explosion.className = 'zap-cannon-explosion';
      explosion.style.left = `${defenderCenterX}px`;
      explosion.style.top = `${defenderCenterY}px`;
      
      container.appendChild(explosion);
      elements.push(explosion);
      
      // Create electric shockwave rings
      for (let i = 0; i < 4; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'zap-cannon-shockwave';
          shockwave.style.left = `${defenderCenterX}px`;
          shockwave.style.top = `${defenderCenterY}px`;
          shockwave.style.setProperty('--shockwave-delay', `${i * 0.1}s`);
          shockwave.style.setProperty('--shockwave-scale', `${2 + i * 0.5}`);
          
          container.appendChild(shockwave);
          elements.push(shockwave);
          
          const shockwaveRemoveTimer = setTimeout(() => {
            if (shockwave.parentNode) {
              shockwave.remove();
            }
          }, 1200);
          timers.push(shockwaveRemoveTimer);
        }, i * 100);
        timers.push(shockwaveTimer);
      }
      
      // Create electric discharge bolts around explosion
      for (let i = 0; i < 20; i++) {
        const boltTimer = setTimeout(() => {
          const bolt = document.createElement('div');
          bolt.className = 'zap-cannon-discharge-bolt';
          
          const boltAngle = Math.random() * Math.PI * 2;
          const boltDistance = 30 + Math.random() * 80;
          bolt.style.left = `${defenderCenterX + Math.cos(boltAngle) * boltDistance}px`;
          bolt.style.top = `${defenderCenterY + Math.sin(boltAngle) * boltDistance}px`;
          
          bolt.style.setProperty('--bolt-length', `${20 + Math.random() * 40}px`);
          bolt.style.setProperty('--bolt-angle', `${boltAngle}rad`);
          bolt.style.setProperty('--bolt-delay', `${i * 0.02}s`);
          
          container.appendChild(bolt);
          elements.push(bolt);
          
          const boltRemoveTimer = setTimeout(() => {
            if (bolt.parentNode) {
              bolt.remove();
            }
          }, 800);
          timers.push(boltRemoveTimer);
        }, i * 30);
        timers.push(boltTimer);
      }
      
      // Create paralysis sparks on defender
      for (let i = 0; i < 12; i++) {
        const paralysisTimer = setTimeout(() => {
          const paralysisSpark = document.createElement('div');
          paralysisSpark.className = 'zap-cannon-paralysis-spark';
          
          const sparkX = defenderCenterX + (Math.random() - 0.5) * 60;
          const sparkY = defenderCenterY + (Math.random() - 0.5) * 60;
          paralysisSpark.style.left = `${sparkX}px`;
          paralysisSpark.style.top = `${sparkY}px`;
          paralysisSpark.style.setProperty('--paralysis-delay', `${i * 0.05}s`);
          
          container.appendChild(paralysisSpark);
          elements.push(paralysisSpark);
          
          const paralysisRemoveTimer = setTimeout(() => {
            if (paralysisSpark.parentNode) {
              paralysisSpark.remove();
            }
          }, 1000);
          timers.push(paralysisRemoveTimer);
        }, i * 50);
        timers.push(paralysisTimer);
      }
      
      // Reset defender after paralysis effect
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('zap-cannon-paralysis');
        defenderEl.style.filter = '';
        defenderEl.style.transform = '';
      }, 800);
      timers.push(defenderResetTimer);
      
    }, 1200); // Explosion timing
    timers.push(explosionTimer);
    
    // PHASE 4: RESET ATTACKER (1800-2200ms)
    const resetTimer = setTimeout(() => {
      // Reset attacker to normal state
      attackerEl.style.transition = 'transform 0.4s ease-out, filter 0.4s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 1800);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2200);
    timers.push(completeTimer);
    
    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('zap-cannon-paralysis');
        defenderEl.style.filter = '';
        defenderEl.style.transform = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.zap-cannon-electric-aura, .zap-cannon-charge-spark, .zap-cannon-electric-field, ' +
        '.zap-cannon-ball, .zap-cannon-trail-spark, .zap-cannon-explosion, ' +
        '.zap-cannon-shockwave, .zap-cannon-discharge-bolt, .zap-cannon-paralysis-spark'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}

ZapCannonAnimation.moveType = "electric";
